import { Injectable, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { select } from '@ngxs/store';
import { asyncScheduler, forkJoin, Observable, of } from 'rxjs';
import { map, catchError, observeOn } from 'rxjs/operators';
import { NonConformanceApiService } from 'src/app/non-conformance-user/services/non-conformance-api.service';
import { NonConformanceStatus } from 'src/app/non-conformance-user/enums/non-conformance-status.enum';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { IListNonConformanceModel } from 'src/app/non-conformance-user/models/list-non-conformance.model';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { IdName } from 'src/app/shared/models/id-name';
import { filterAllDepsToken } from 'src/app/shared/functions/filter-all-deps-token';

export interface FilteredNonConformances {
  open: IListNonConformanceModel[];
  completed: IListNonConformanceModel[];
  closed: IListNonConformanceModel[];
}

@Injectable({
  providedIn: 'root',
})
export class DeviationsOverTimeDataService {
  private readonly injector = inject(Injector);

  private readonly allAvailableDepartments = select(
    TenantUserAccessState.getAllAvailableDepartments
  );

  constructor(private nonConformanceApiService: NonConformanceApiService) {}

  loadData(selectedVesselIds: string[]): Observable<FilteredNonConformances> {
    const tempFilterObj = new MainTableFilterModel(MainTableFilterModulesEnum.NonConformances);
    tempFilterObj.pageIndex = 0;
    tempFilterObj.pageSize = 10000;

    // Tenant-level list with optional DepartmentIds (same pattern as ReportsStatus); client still
    // filters when multiple vessels are selected in the widget.
    return forkJoin([
      this.nonConformanceApiService.getAllNonConformancesWithStates(
        [NonConformanceStatus.Open, NonConformanceStatus.Completed, NonConformanceStatus.Closed],
        tempFilterObj,
        true,
        selectedVesselIds
      ),
    ]).pipe(
      map(([response]) => ({
        open:
          response.data?.filter((nc) => nc.nonConformanceState === NonConformanceStatus.Open) ?? [],
        completed:
          response.data?.filter(
            (nc) => nc.nonConformanceState === NonConformanceStatus.Completed
          ) ?? [],
        closed:
          response.data?.filter((nc) => nc.nonConformanceState === NonConformanceStatus.Closed) ??
          [],
      })),
      catchError((error) => {
        console.error('Failed to load deviations data:', error);
        // Return empty arrays on error to prevent component crash
        return of({ open: [], completed: [], closed: [] });
      })
    );
  }

  applyFilters(
    allOpenNonConformances: IListNonConformanceModel[],
    allCompletedNonConformances: IListNonConformanceModel[],
    allClosedNonConformances: IListNonConformanceModel[],
    selectedVesselIds: string[],
    selectedTypeIds: string[],
    allDeviationOptions: IdName[]
  ): FilteredNonConformances {
    let filteredOpen = [...allOpenNonConformances];
    let filteredCompleted = [...allCompletedNonConformances];
    let filteredClosed = [...allClosedNonConformances];

    // Filter by department(s) only when real department GUIDs are selected (not all-deps token).
    // Empty selection means show all (no filtering).
    const effectiveDepartmentIds = filterAllDepsToken(selectedVesselIds);
    if (effectiveDepartmentIds.length > 0) {
      filteredOpen = this.filterByDepartments(filteredOpen, effectiveDepartmentIds);
      filteredCompleted = this.filterByDepartments(filteredCompleted, effectiveDepartmentIds);
      filteredClosed = this.filterByDepartments(filteredClosed, effectiveDepartmentIds);
    }

    // Filter by type(s) if specific type(s) are selected
    if (this.shouldFilterByType(selectedTypeIds, allDeviationOptions)) {
      filteredOpen = this.filterByType(filteredOpen, selectedTypeIds);
      filteredCompleted = this.filterByType(filteredCompleted, selectedTypeIds);
      filteredClosed = this.filterByType(filteredClosed, selectedTypeIds);
    }

    return {
      open: filteredOpen,
      completed: filteredCompleted,
      closed: filteredClosed,
    };
  }

  loadAvailableDepartments(): Observable<IDepartmentInfo[]> {
    // Get departments from the currently selected tenant only.
    // observeOn avoids synchronous emissions from toObservable during subscribe, which would update
    // bound template values in the same CD turn and trigger NG0100 (ExpressionChangedAfterItHasBeenChecked).
    return toObservable(this.allAvailableDepartments, { injector: this.injector }).pipe(
      observeOn(asyncScheduler),
      map((departments) => {
        if (departments && departments.length > 0) {
          return [...departments].sort((a, b) => a.name.localeCompare(b.name));
        }
        return [];
      })
    );
  }

  extractUniqueTypes(data: IListNonConformanceModel[]): string[] {
    const typeSet = new Set<string>();
    data.forEach((item) => {
      const typeName = item.type?.name;
      if (typeName) {
        typeSet.add(typeName);
      }
    });
    return Array.from(typeSet).sort();
  }

  updateAllDeviationOptions(data: IListNonConformanceModel[]): IdName[] {
    const uniqueTypes = this.extractUniqueTypes(data);
    return uniqueTypes.map((typeName) => ({
      id: typeName,
      name: typeName,
    }));
  }

  private filterByDepartment(
    items: IListNonConformanceModel[],
    departmentId: string
  ): IListNonConformanceModel[] {
    return items.filter((nc) => nc.department?.id === departmentId);
  }

  private filterByDepartments(
    items: IListNonConformanceModel[],
    departmentIds: string[]
  ): IListNonConformanceModel[] {
    return items.filter((nc) => nc.department?.id && departmentIds.includes(nc.department.id));
  }

  private filterByType(
    items: IListNonConformanceModel[],
    selectedTypeIds: string[]
  ): IListNonConformanceModel[] {
    return items.filter((nc) => {
      const typeName = nc.type?.name;
      return typeName && selectedTypeIds.includes(typeName);
    });
  }

  private shouldFilterByType(selectedTypeIds: string[], allDeviationOptions: IdName[]): boolean {
    if (!selectedTypeIds || !Array.isArray(selectedTypeIds) || selectedTypeIds.length === 0) {
      return false;
    }
    return selectedTypeIds.length !== allDeviationOptions.length;
  }
}
